<template>
	<div class="p-4 md:p-6 flex-1 overflow-y-auto font-sans">
		<!-- ── Section Header ── -->
		<div
			class="flex items-center justify-between mb-6 pb-5 border-b border-primary-100 dark:border-dark-600">
			<div class="flex items-center gap-3">
				<div
					class="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-400/15 flex items-center justify-center flex-shrink-0">
					<svg
						class="w-5 h-5 text-primary-400"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 20.25a48.25 48.25 0 01-8.135-.687c-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
					</svg>
				</div>
				<div>
					<h1
						class="font-display text-xl md:text-2xl font-extrabold tracking-tight text-dark-700 dark:text-white">
						Beta Testing
					</h1>
					<p class="text-[13px] text-muted dark:text-muted-light mt-0.5">
						Join early access and help us shape the app
					</p>
				</div>
			</div>
		</div>

		<Breadcrumbs />

		<!-- ══════════ BETA SIGNUP ══════════ -->
		<div class="space-y-5">
			<!-- Intro CTA card -->
			<div
				class="bg-surface dark:bg-dark-700 rounded-card shadow-card border border-dashed border-primary-200 dark:border-primary-400/25 flex flex-col items-center py-14 px-8 text-center">
				<div
					class="w-14 h-14 rounded-2xl bg-primary-50 dark:bg-primary-400/15 flex items-center justify-center mb-5">
					<svg
						class="w-7 h-7 text-primary-400"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 20.25a48.25 48.25 0 01-8.135-.687c-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
					</svg>
				</div>

				<h2
					class="font-display text-[18px] font-bold text-dark-700 dark:text-white mb-3">
					Become a Beta Tester
				</h2>

				<p
					class="text-[13px] text-muted dark:text-muted-light leading-relaxed max-w-xl mb-8">
					We're rolling out early access before our public launch. Join the
					beta group to get approved, then download the app to start testing
					new features before anyone else.
				</p>

				<!-- Steps -->
				<div
					class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8 w-full max-w-2xl text-left">
					<div
						class="flex items-start gap-3 p-4 rounded-xl bg-primary-50/60 dark:bg-dark-600/60 border border-primary-100 dark:border-dark-500">
						<div
							class="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-extrabold text-white flex-shrink-0"
							:class="joinedGroup ? 'bg-emerald-500' : 'bg-primary-400'">
							<svg
								v-if="joinedGroup"
								class="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="3"
									d="M5 13l4 4L19 7" />
							</svg>
							<span v-else>1</span>
						</div>
						<div class="flex-1">
							<p
								class="text-[13px] font-semibold text-dark-700 dark:text-white">
								Join the Beta Group
							</p>
							<p
								class="text-[11px] text-muted dark:text-muted-light mt-0.5 leading-relaxed mb-3">
								Request access to our Google Group — this is what whitelists
								your account for the beta build.
							</p>
							<button
								@click="joinGroup"
								class="inline-flex items-center gap-1.5 text-[12px] font-bold text-primary-600 dark:text-primary-400 hover:underline">
								Join Google Group
								<svg
									class="w-3.5 h-3.5"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2.5"
										d="M17 8l4 4m0 0l-4 4m4-4H3" />
								</svg>
							</button>
						</div>
					</div>

					<div
						class="flex items-start gap-3 p-4 rounded-xl bg-primary-50/60 dark:bg-dark-600/60 border border-primary-100 dark:border-dark-500"
						:class="{ 'opacity-60': !joinedGroup }">
						<div
							class="w-7 h-7 rounded-full bg-primary-400 flex items-center justify-center text-[11px] font-extrabold text-white flex-shrink-0">
							2
						</div>
						<div class="flex-1">
							<p
								class="text-[13px] font-semibold text-dark-700 dark:text-white">
								Download the App
							</p>
							<p
								class="text-[11px] text-muted dark:text-muted-light mt-0.5 leading-relaxed mb-3">
								Once you're approved in the group, grab the latest beta build
								for your device.
							</p>
							<button
								@click="downloadAndroid"
								class="inline-flex items-center gap-1.5 text-[12px] font-bold text-primary-600 dark:text-primary-400 hover:underline">
								Download for Android
								<svg
									class="w-3.5 h-3.5"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2.5"
										d="M17 8l4 4m0 0l-4 4m4-4H3" />
								</svg>
							</button>
						</div>
					</div>
				</div>

				<!-- Platform buttons -->
				<div class="flex flex-col sm:flex-row items-center gap-3">
					<button
						@click="joinGroup"
						class="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-display font-bold text-[14px] bg-primary-400 text-white hover:bg-primary-600 hover:-translate-y-0.5 shadow-teal transition-all duration-200">
						<svg
							class="w-4 h-4"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2.5"
								d="M17.25 6.75L22.5 12l-5.25 5.25M6.75 17.25L1.5 12l5.25-5.25" />
						</svg>
						1. Join Beta Group
					</button>

					<button
						@click="downloadAndroid"
						class="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-display font-bold text-[14px] border transition-all duration-200"
						:class="
							joinedGroup
								? 'bg-white dark:bg-dark-700 text-dark-700 dark:text-white border-primary-200 dark:border-dark-500 hover:-translate-y-0.5 shadow-card hover:shadow-card-hover'
								: 'bg-primary-50 dark:bg-dark-600 text-muted dark:text-muted-light border-primary-100 dark:border-dark-500 cursor-not-allowed'
						">
						<svg
							class="w-4 h-4"
							fill="currentColor"
							viewBox="0 0 24 24">
							<path
								d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24a11.463 11.463 0 00-9.02 0L5.65 5.67a.637.637 0 00-.87-.2c-.28.16-.42.53-.26.85L6.36 9.5C3.99 11.14 2.4 13.86 2.4 17h19.2c0-3.14-1.59-5.86-3.96-7.52zM7.5 15a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm9 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" />
						</svg>
						2. Download for Android
					</button>

					<span
						class="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-display font-bold text-[14px] bg-primary-50 dark:bg-dark-600 text-muted dark:text-muted-light border border-dashed border-primary-200 dark:border-dark-500 cursor-not-allowed">
						<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
							<path
								d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.5 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.08-.42 1.44-1.38 2.82zM13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
						</svg>
						iOS — Coming Soon
					</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import Breadcrumbs from "@/components/Breadcrumbs.vue";
import { showToast } from "@/constants/toast";

const BETA_GROUP_URL = "https://groups.google.com/g/trekko-beta-testers";
// TODO: replace with the real Play Store listing URL once published
const ANDROID_DOWNLOAD_URL =
	"https://play.google.com/store/apps/details?id=com.trekko.app";

export default {
	name: "BetaTesting",
	components: { Breadcrumbs },
	data() {
		return {
			joinedGroup: false,
		};
	},
	methods: {
		joinGroup() {
			window.open(BETA_GROUP_URL, "_blank", "noopener,noreferrer");
			this.joinedGroup = true;
			try {
				localStorage.setItem("trekko_beta_joined_group", "1");
			} catch (e) {
				// ignore storage errors (private browsing, etc.)
			}
		},
		downloadAndroid() {
			if (!this.joinedGroup) {
				showToast(
					"info",
					"Join the beta group first so the app recognizes your account.",
				);
			}
			window.open(ANDROID_DOWNLOAD_URL, "_blank", "noopener,noreferrer");
		},
	},
	mounted() {
		try {
			this.joinedGroup =
				localStorage.getItem("trekko_beta_joined_group") === "1";
		} catch (e) {
			this.joinedGroup = false;
		}
	},
};
</script>